import { CheckOutlined } from '@ant-design/icons';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type IProductClaimHit } from '@mahawi/eshop-common/src/types';
import { Button, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import { getColumnSearch } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  productClaimAccept,
  reportProductClaimsLoad,
} from '../reducers/product-safety/reducer';
import { SPACING } from '../theme/tokens';

export const CLAIM_NOTE_MIN_LENGTH = 10;

interface IRow extends IProductClaimHit {
  key: string;
}

export const filterClaimHits = (
  hits: IProductClaimHit[],
  languageCode: string | undefined,
): IProductClaimHit[] =>
  languageCode
    ? hits.filter(
        (hit: IProductClaimHit): boolean => hit.languageCode === languageCode,
      )
    : hits;

const hitKey = (hit: IProductClaimHit): string =>
  `${hit.productUuid}:${hit.languageCode}:${hit.field}:${hit.term}`;

function ReportProductClaimsContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const claimHits = useSelector((s: RootState) => s.ProductSafety.claimHits);
  const inProcess = useSelector((s: RootState) => s.ProductSafety.inProcess);
  const [languageCode, setLanguageCode] = useState<string | undefined>();
  const [accepting, setAccepting] = useState<IProductClaimHit | null>(null);
  const [note, setNote] = useState<string>('');

  useEffect((): void => {
    dispatch(reportProductClaimsLoad());
  }, [dispatch]);

  const languageOptions = useMemo(
    () =>
      [
        ...new Set(
          (claimHits ?? []).map((hit: IProductClaimHit) => hit.languageCode),
        ),
      ]
        .sort(stringCompare)
        .map((code: string) => ({ label: code, value: code })),
    [claimHits],
  );

  const dataSource: IRow[] = useMemo(
    (): IRow[] =>
      filterClaimHits(claimHits ?? [], languageCode).map(
        (hit: IProductClaimHit): IRow => ({ ...hit, key: hitKey(hit) }),
      ),
    [claimHits, languageCode],
  );

  const columns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: IRow, b: IRow): number => stringCompare(a.name, b.name),
      ...getColumnSearch<IRow>('name'),
      render: (_value: string, row: IRow): React.ReactElement => (
        <Link
          to={`/product/${row.productUuid}`}
          label={row.name}
          openInNewTab
        />
      ),
    },
    { title: 'Language', dataIndex: 'languageCode', key: 'languageCode' },
    { title: 'Field', dataIndex: 'field', key: 'field' },
    {
      title: 'Term',
      dataIndex: 'term',
      key: 'term',
      render: (term: string): React.ReactElement => (
        <Tag color="orange">{term}</Tag>
      ),
    },
    { title: 'Excerpt', dataIndex: 'excerpt', key: 'excerpt' },
    {
      title: '',
      key: 'actions',
      render: (_value: string, row: IRow): React.ReactElement => (
        <Button
          size="small"
          icon={<CheckOutlined />}
          onClick={(): void => {
            setNote('');
            setAccepting(row);
          }}
        >
          Mark as substantiated
        </Button>
      ),
    },
  ];

  const isNoteValid: boolean = note.trim().length >= CLAIM_NOTE_MIN_LENGTH;

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Environmental and durability claims"
        onReload={(): void => {
          dispatch(reportProductClaimsLoad());
        }}
        isReloadDisabled={inProcess}
      />

      <Space size={SPACING.lg} align="center">
        <Select
          allowClear
          placeholder="Filter by language"
          style={{ width: 200 }}
          options={languageOptions}
          value={languageCode}
          onChange={(value: string | undefined): void => setLanguageCode(value)}
        />
        <Typography.Text>{dataSource.length} hits</Typography.Text>
      </Space>

      <Table
        columns={columns as Parameters<typeof Table>[0]['columns']}
        dataSource={dataSource}
        isLoading={inProcess && !claimHits}
      />

      <Modal
        open={accepting !== null}
        title="Mark the claim as substantiated"
        okText="Accept claim"
        okButtonProps={{ disabled: !isNoteValid }}
        onCancel={(): void => setAccepting(null)}
        onOk={(): void => {
          if (accepting && isNoteValid) {
            dispatch(
              productClaimAccept({
                productUuid: accepting.productUuid,
                languageCode: accepting.languageCode,
                field: accepting.field,
                term: accepting.term,
                note: note.trim(),
              }),
            );
            setAccepting(null);
          }
        }}
      >
        <Typography.Paragraph>
          {accepting ? `${accepting.term}: ${accepting.excerpt}` : ''}
        </Typography.Paragraph>
        <Input.TextArea
          rows={4}
          value={note}
          placeholder="Evidence that substantiates the claim (at least 10 characters)"
          onChange={(event): void => setNote(event.target.value)}
        />
      </Modal>
    </Space>
  );
}

export default ReportProductClaimsContainer;
